import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import AppLayout from "@/components/AppLayout";
import AdminLayout from "@/components/AdminLayout";
import PublicLayout from "@/components/PublicLayout";
import Spinner from "@/components/Spinner";

// ── Auth ──────────────────────────────────────────────────────
import LoginPage    from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";

// ── App ───────────────────────────────────────────────────────
import DashboardPage       from "@/pages/DashboardPage";
import CampaignsPage       from "@/pages/CampaignsPage";
import CampaignWizardPage  from "@/pages/CampaignWizardPage";
import CampaignEditorPage  from "@/pages/CampaignEditorPage";
import CampaignSendLogPage from "@/pages/CampaignSendLogPage";
import AiCampaignGeneratorPage from "@/pages/AiCampaignGeneratorPage";
import SubscribersPage     from "@/pages/SubscribersPage";
import AnalyticsPage       from "@/pages/AnalyticsPage";
import EngagementDrilldownPage from "@/pages/EngagementDrilldownPage";
import AutomationsPage     from "@/pages/AutomationsPage";
import DeliverabilityPage  from "@/pages/DeliverabilityPage";
import SettingsPage        from "@/pages/SettingsPage";
import LeadsPage           from "@/pages/LeadsPage";
import IntentSettingsPage  from "@/pages/IntentSettingsPage";
import IntegrationsPage    from "@/pages/IntegrationsPage";
import MarketplacePage     from "@/pages/network/MarketplacePage";
import MyOffersPage        from "@/pages/network/MyOffersPage";
import EarningsPage        from "@/pages/network/EarningsPage";
import TemplateEditorPage  from "@/pages/TemplateEditorPage";
import PrebuiltLibraryPage from "@/pages/PrebuiltLibraryPage";
import PrebuiltDetailPage  from "@/pages/PrebuiltDetailPage";
import PrebuiltAutomationsPage      from "@/pages/PrebuiltAutomationsPage";
import PrebuiltAutomationDetailPage from "@/pages/PrebuiltAutomationDetailPage";
import EmailHealthPage              from "@/pages/EmailHealthPage";
import RecommendationsPage          from "@/pages/RecommendationsPage";
import AbTestArchivePage            from "@/pages/AbTestArchivePage";
import CompetitorTrackerPage        from "@/pages/CompetitorTrackerPage";
import LandingPagesPage             from "@/pages/LandingPagesPage";
import UserInboxPage                from "@/pages/UserInboxPage";

// ── Admin ─────────────────────────────────────────────────────
import AdminOverviewPage  from "@/pages/admin/AdminOverviewPage";
import AdminUsersPage     from "@/pages/admin/AdminUsersPage";
import AdminMailPage      from "@/pages/admin/AdminMailPage";
import AdminInboxPage     from "@/pages/admin/AdminInboxPage";
import AdminUsagePage     from "@/pages/admin/AdminUsagePage";
import AdminAuditLogPage  from "@/pages/admin/AdminAuditLogPage";
import AdminPlansPage    from "@/pages/admin/AdminPlansPage";
import AdminSeedPage     from "@/pages/admin/AdminSeedPage";
import AdminVerticalsPage from "@/pages/admin/AdminVerticalsPage";
import AdminPromptsPage from "@/pages/admin/AdminPromptsPage";

// ── Public / SEO ──────────────────────────────────────────────
import HomePage    from "@/pages/public/HomePage";
import FeaturesPage from "@/pages/public/FeaturesPage";
import PricingPage  from "@/pages/public/PricingPage";
import BlogPage     from "@/pages/public/BlogPage";
import BlogPostPage from "@/pages/public/BlogPostPage";
import SeoPageView  from "@/pages/public/SeoPageView";
import AiCampaignBuilderPage      from "@/pages/public/features/AiCampaignBuilderPage";
import SubjectLineOptimisationPage from "@/pages/public/features/SubjectLineOptimisationPage";
import SpamScorePage              from "@/pages/public/features/SpamScorePage";
import InboxPreviewPage           from "@/pages/public/features/InboxPreviewPage";

// ── New Feature Landing Pages (March 2026) ────────────────────
import AiCampaignGeneratorLandingPage from "@/pages/public/features/AiCampaignGeneratorLandingPage";
import AutoOptimizationLandingPage    from "@/pages/public/features/AutoOptimizationLandingPage";
import BehavioralTriggersLandingPage  from "@/pages/public/features/BehavioralTriggersLandingPage";
import EngagementScoringLandingPage   from "@/pages/public/features/EngagementScoringLandingPage";
import SendTimeOptimizationLandingPage from "@/pages/public/features/SendTimeOptimizationLandingPage";
import RevenueAttributionLandingPage  from "@/pages/public/features/RevenueAttributionLandingPage";
import ListHygieneLandingPage         from "@/pages/public/features/ListHygieneLandingPage";
import PrebuiltCampaignsLandingPage   from "@/pages/public/PrebuiltCampaignsLandingPage";
import PrebuiltAutomationsLandingPage from "@/pages/public/PrebuiltAutomationsLandingPage";
import IntegrationDirectoryPage       from "@/pages/public/IntegrationDirectoryPage";
import AcademyPage                    from "@/pages/public/AcademyPage";
import {
  EmailHealthLandingPage,
  RecommendationsLandingPage,
  AbTestLandingPage,
  CompetitorTrackerLandingPage,
  LandingPagesLandingPage,
} from "@/pages/public/FeatureLandingPages";

// ── Free Tools ────────────────────────────────────────────────
import ToolsIndexPage          from "@/pages/public/tools/ToolsIndexPage";
import SpamCheckerToolPage      from "@/pages/public/tools/SpamCheckerToolPage";
import SubjectLineTesterPage    from "@/pages/public/tools/SubjectLineTesterPage";
import WarmupCalculatorPage     from "@/pages/public/tools/WarmupCalculatorPage";
import ColdEmailGeneratorPage   from "@/pages/public/tools/ColdEmailGeneratorPage";
import BlacklistCheckerPage     from "@/pages/public/tools/BlacklistCheckerPage";
import DeliverabilityGraderPage from "@/pages/public/tools/DeliverabilityGraderPage";
import RoiCalculatorPage       from "@/pages/public/tools/RoiCalculatorPage";
import SignatureGeneratorPage  from "@/pages/public/tools/SignatureGeneratorPage";
import DmarcCheckerPage        from "@/pages/public/tools/DmarcCheckerPage";
import EmailCopyAnalyzerPage   from "@/pages/public/tools/EmailCopyAnalyzerPage";
import ListGrowthCalculatorPage from "@/pages/public/tools/ListGrowthCalculatorPage";
import NewsletterBenchmarkPage  from "@/pages/public/tools/NewsletterBenchmarkPage";

import "./styles/globals.css";

// ── Route guards ──────────────────────────────────────────────
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner className="h-screen" />;
  return user ? <>{children}</> : <Navigate to="/login" replace />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner className="h-screen" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner className="h-screen" />;
  return user ? <Navigate to="/dashboard" replace /> : <>{children}</>;
}

// ── App ───────────────────────────────────────────────────────
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Auth — before public catch-all */}
          <Route path="/login"    element={<PublicRoute><LoginPage    /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Protected app — before public catch-all */}
          <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
            <Route path="/dashboard"          element={<DashboardPage      />} />
            <Route path="/inbox"              element={<UserInboxPage      />} />
            <Route path="/campaigns"          element={<CampaignsPage      />} />
            <Route path="/campaigns/wizard"   element={<CampaignWizardPage />} />
            <Route path="/campaigns/generate" element={<AiCampaignGeneratorPage />} />
            <Route path="/campaigns/prebuilt" element={<PrebuiltLibraryPage />} />
            <Route path="/campaigns/prebuilt/:slug" element={<PrebuiltDetailPage />} />
            <Route path="/campaigns/:id/send-log" element={<CampaignSendLogPage />} />
            <Route path="/campaigns/:id"      element={<CampaignEditorPage />} />
            <Route path="/subscribers"        element={<SubscribersPage    />} />
            <Route path="/analytics"          element={<AnalyticsPage      />} />
            <Route path="/engagement"         element={<EngagementDrilldownPage />} />
            <Route path="/automations"        element={<AutomationsPage    />} />
            <Route path="/automations/prebuilt" element={<PrebuiltAutomationsPage />} />
            <Route path="/automations/prebuilt/:slug" element={<PrebuiltAutomationDetailPage />} />
            <Route path="/deliverability"     element={<DeliverabilityPage />} />
            <Route path="/email-health"        element={<EmailHealthPage />} />
            <Route path="/recommendations"     element={<RecommendationsPage />} />
            <Route path="/ab-tests"            element={<AbTestArchivePage />} />
            <Route path="/competitor-tracker"  element={<CompetitorTrackerPage />} />
            <Route path="/landing-pages"       element={<LandingPagesPage />} />
            <Route path="/templates/editor"   element={<TemplateEditorPage />} />
            <Route path="/settings"           element={<SettingsPage       />} />
            <Route path="/leads"              element={<LeadsPage          />} />
            <Route path="/leads/settings"     element={<IntentSettingsPage />} />
            <Route path="/integrations"       element={<IntegrationsPage   />} />
            <Route path="/network"            element={<MarketplacePage    />} />
            <Route path="/network/my-offers"  element={<MyOffersPage       />} />
            <Route path="/network/earnings"   element={<EarningsPage       />} />
          </Route>

          {/* Admin panel — before public catch-all */}
          <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route path="/admin"            element={<AdminOverviewPage  />} />
            <Route path="/admin/users"      element={<AdminUsersPage     />} />
            <Route path="/admin/plans"      element={<AdminPlansPage     />} />
            <Route path="/admin/mail"       element={<AdminMailPage      />} />
            <Route path="/admin/inbox"      element={<AdminInboxPage     />} />
            <Route path="/admin/usage"      element={<AdminUsagePage     />} />
            <Route path="/admin/audit-log"  element={<AdminAuditLogPage  />} />
            <Route path="/admin/seed"       element={<AdminSeedPage      />} />
            <Route path="/admin/verticals" element={<AdminVerticalsPage />} />
            <Route path="/admin/prompts"   element={<AdminPromptsPage />} />
          </Route>

          {/* ── 301 Redirects: old /features/* and /tools/* → flat routes ── */}
          <Route path="/features/ai-campaign-builder"       element={<Navigate to="/ai-campaign-builder" replace />} />
          <Route path="/features/ai-campaign-generator"     element={<Navigate to="/ai-campaign-generator" replace />} />
          <Route path="/features/subject-line-optimisation" element={<Navigate to="/subject-line-optimisation" replace />} />
          <Route path="/features/auto-optimization"         element={<Navigate to="/auto-optimization" replace />} />
          <Route path="/features/send-time-optimization"    element={<Navigate to="/send-time-optimization" replace />} />
          <Route path="/features/spam-score"                element={<Navigate to="/spam-score" replace />} />
          <Route path="/features/inbox-preview"             element={<Navigate to="/inbox-preview" replace />} />
          <Route path="/features/engagement-scoring"        element={<Navigate to="/engagement-scoring" replace />} />
          <Route path="/features/behavioral-triggers"       element={<Navigate to="/behavioral-triggers" replace />} />
          <Route path="/features/revenue-attribution"       element={<Navigate to="/revenue-attribution" replace />} />
          <Route path="/features/list-hygiene"              element={<Navigate to="/list-hygiene" replace />} />
          <Route path="/tools/spam-checker"                 element={<Navigate to="/spam-checker" replace />} />
          <Route path="/tools/subject-line-tester"          element={<Navigate to="/subject-line-tester" replace />} />
          <Route path="/tools/warmup-calculator"            element={<Navigate to="/warmup-calculator" replace />} />
          <Route path="/tools/cold-email-generator"         element={<Navigate to="/cold-email-generator" replace />} />
          <Route path="/tools/blacklist-checker"            element={<Navigate to="/blacklist-checker" replace />} />
          <Route path="/tools/deliverability-grader"        element={<Navigate to="/deliverability-grader" replace />} />
          <Route path="/tools/roi-calculator"               element={<Navigate to="/roi-calculator" replace />} />
          <Route path="/tools/email-signature-generator"    element={<Navigate to="/email-signature-generator" replace />} />
          <Route path="/tools/dmarc-checker"                element={<Navigate to="/dmarc-checker" replace />} />
          <Route path="/tools/email-copy-analyzer"           element={<Navigate to="/email-copy-analyzer" replace />} />
          <Route path="/tools/list-growth-calculator"        element={<Navigate to="/list-growth-calculator" replace />} />
          <Route path="/tools/newsletter-benchmark"          element={<Navigate to="/newsletter-benchmark" replace />} />

          {/* Public marketing site — /:slug catch-all MUST be last */}
          <Route element={<PublicLayout />}>
            <Route path="/"           element={<HomePage    />} />
            <Route path="/features"   element={<FeaturesPage />} />

            {/* Existing feature pages */}
            <Route path="/ai-campaign-builder"       element={<AiCampaignBuilderPage />} />
            <Route path="/subject-line-optimisation" element={<SubjectLineOptimisationPage />} />
            <Route path="/spam-score"                element={<SpamScorePage />} />
            <Route path="/inbox-preview"             element={<InboxPreviewPage />} />

            {/* ── NEW: March 2026 Feature Landing Pages ──────────── */}
            <Route path="/ai-campaign-generator"  element={<AiCampaignGeneratorLandingPage />} />
            <Route path="/auto-optimization"      element={<AutoOptimizationLandingPage />} />
            <Route path="/behavioral-triggers"    element={<BehavioralTriggersLandingPage />} />
            <Route path="/engagement-scoring"     element={<EngagementScoringLandingPage />} />
            <Route path="/send-time-optimization" element={<SendTimeOptimizationLandingPage />} />
            <Route path="/revenue-attribution"    element={<RevenueAttributionLandingPage />} />
            <Route path="/list-hygiene"           element={<ListHygieneLandingPage />} />
            <Route path="/prebuilt-campaigns"    element={<PrebuiltCampaignsLandingPage />} />
            <Route path="/prebuilt-automations"  element={<PrebuiltAutomationsLandingPage />} />
            <Route path="/integrations-directory" element={<IntegrationDirectoryPage />} />
            <Route path="/academy"               element={<AcademyPage />} />
            <Route path="/email-health-dashboard"  element={<EmailHealthLandingPage />} />
            <Route path="/smart-recommendations"   element={<RecommendationsLandingPage />} />
            <Route path="/ab-test-insights"        element={<AbTestLandingPage />} />
            <Route path="/competitor-email-tracker" element={<CompetitorTrackerLandingPage />} />
            <Route path="/landing-page-builder"    element={<LandingPagesLandingPage />} />

            <Route path="/pricing"    element={<PricingPage  />} />
            <Route path="/blog"       element={<BlogPage     />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/tools"                       element={<ToolsIndexPage           />} />
            <Route path="/spam-checker"          element={<SpamCheckerToolPage      />} />
            <Route path="/subject-line-tester"   element={<SubjectLineTesterPage    />} />
            <Route path="/warmup-calculator"     element={<WarmupCalculatorPage     />} />
            <Route path="/cold-email-generator"  element={<ColdEmailGeneratorPage   />} />
            <Route path="/blacklist-checker"     element={<BlacklistCheckerPage     />} />
            <Route path="/deliverability-grader" element={<DeliverabilityGraderPage />} />
            <Route path="/roi-calculator"        element={<RoiCalculatorPage />} />
            <Route path="/email-signature-generator" element={<SignatureGeneratorPage />} />
            <Route path="/dmarc-checker"             element={<DmarcCheckerPage />} />
            <Route path="/email-copy-analyzer"     element={<EmailCopyAnalyzerPage />} />
            <Route path="/list-growth-calculator"  element={<ListGrowthCalculatorPage />} />
            <Route path="/newsletter-benchmark"    element={<NewsletterBenchmarkPage />} />
            <Route path="/:slug"      element={<SeoPageView  />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
